import { useCallback, useMemo, useState } from 'react';

export type TableLayoutMode = 'fit' | 'scroll';

const DEFAULT_MODE: TableLayoutMode = 'fit';
const MIN_COLUMN_WIDTH_PX = 60;
const AUTO_FIT_MAX_PX = 600;
const AUTO_FIT_PADDING_PX = 24;

/**
 * Returns the intrinsic content width of a grid cell, regardless of the
 * current track width. scrollWidth alone only reports overflow — when
 * content is narrower than the cell it just returns the cell width, so we
 * clone the cell into an offscreen container and let it size to content.
 */
function measureCellContentWidth(el: HTMLElement): number {
  const clone = el.cloneNode(true) as HTMLElement;
  const computed = window.getComputedStyle(el);
  clone.style.cssText = '';
  clone.style.position = 'absolute';
  clone.style.visibility = 'hidden';
  clone.style.top = '-99999px';
  clone.style.left = '-99999px';
  clone.style.width = 'auto';
  clone.style.minWidth = '0';
  clone.style.maxWidth = 'none';
  clone.style.height = 'auto';
  clone.style.whiteSpace = 'nowrap';
  clone.style.overflow = 'visible';
  clone.style.display = 'inline-block';
  clone.style.font = computed.font;
  clone.style.fontFamily = computed.fontFamily;
  clone.style.fontSize = computed.fontSize;
  clone.style.fontWeight = computed.fontWeight;
  clone.style.letterSpacing = computed.letterSpacing;
  clone.style.padding = computed.padding;
  // Header cells contain nested grids whose children carry min-width: 0 and
  // overflow: hidden — that collapses the label when the clone is free-flowing.
  // Release those constraints on every descendant so the clone sizes to its
  // natural content (including header label + sort/filter icons).
  clone.querySelectorAll<HTMLElement>('*').forEach((child) => {
    child.style.minWidth = '0';
    child.style.maxWidth = 'none';
    child.style.width = 'auto';
    child.style.overflow = 'visible';
    child.style.textOverflow = 'clip';
    child.style.whiteSpace = 'nowrap';
  });
  document.body.appendChild(clone);
  const width = clone.getBoundingClientRect().width;
  clone.remove();
  return width;
}

interface TableLayoutState {
  mode: TableLayoutMode;
  /** Column-width fractions (sum to ~1) used by the fit layout. */
  fitWidths: Record<string, number>;
  /** Absolute pixel widths used by the scroll layout. */
  scrollWidths: Record<string, number>;
}

const EMPTY_STATE: TableLayoutState = {
  mode: DEFAULT_MODE,
  fitWidths: {},
  scrollWidths: {},
};

function storageKeyFor(tableKey: string) {
  return `bmwe-table-state:${tableKey}`;
}

function sanitizeNumericMap(raw: unknown): Record<string, number> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, number> = {};
  for (const [id, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) {
      out[id] = v;
    }
  }
  return out;
}

function loadState(tableKey: string | undefined): TableLayoutState {
  if (!tableKey || typeof window === 'undefined') return EMPTY_STATE;
  try {
    const raw = window.localStorage.getItem(storageKeyFor(tableKey));
    if (!raw) return EMPTY_STATE;
    const parsed = JSON.parse(raw) as Partial<TableLayoutState> | null;
    if (!parsed || typeof parsed !== 'object') return EMPTY_STATE;
    const mode: TableLayoutMode =
      parsed.mode === 'scroll' || parsed.mode === 'fit'
        ? parsed.mode
        : DEFAULT_MODE;
    return {
      mode,
      fitWidths: sanitizeNumericMap(parsed.fitWidths),
      scrollWidths: sanitizeNumericMap(parsed.scrollWidths),
    };
  } catch {
    return EMPTY_STATE;
  }
}

function saveState(tableKey: string | undefined, state: TableLayoutState) {
  if (!tableKey || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKeyFor(tableKey), JSON.stringify(state));
  } catch {
    // ignore quota / serialization errors
  }
}

function fractionsToWidthMap(
  fractions: Record<string, number>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [id, f] of Object.entries(fractions)) {
    out[id] = `${f}fr`;
  }
  return out;
}

function pixelsToWidthMap(
  pixels: Record<string, number>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [id, px] of Object.entries(pixels)) {
    out[id] = `${px}px`;
  }
  return out;
}

/**
 * Unified per-table state stored in a single localStorage entry per `tableKey`.
 * Keeps the layout mode and the two width sets (fit fractions, scroll pixels)
 * together so they evolve atomically when the user toggles modes or resizes
 * columns.
 */
export function useTableLayout(tableKey: string) {
  const [state, setState] = useState<TableLayoutState>(() =>
    loadState(tableKey),
  );

  const toggleLayoutMode = useCallback(() => {
    setState((prev) => {
      const next: TableLayoutState = {
        ...prev,
        mode: prev.mode === 'fit' ? 'scroll' : 'fit',
      };
      saveState(tableKey, next);
      return next;
    });
  }, [tableKey]);

  /** Fit-mode resize: drag splits width between active column and right neighbor. */
  const startFitResize = useCallback(
    (columnId: string, th: HTMLElement) => {
      const row = th.parentElement;
      const initialPixels: Record<string, number> = {};
      let total = 0;
      if (row) {
        for (const sibling of Array.from(row.children)) {
          if (!(sibling instanceof HTMLElement)) continue;
          const siblingId = sibling.getAttribute('data-column-id');
          if (!siblingId) continue;
          const w = sibling.getBoundingClientRect().width;
          initialPixels[siblingId] = w;
          total += w;
        }
      }
      const startActiveWidth =
        initialPixels[columnId] ?? th.getBoundingClientRect().width;

      let neighborId: string | null = null;
      let cursor = th.nextElementSibling;
      while (cursor) {
        if (cursor instanceof HTMLElement) {
          const id = cursor.getAttribute('data-column-id');
          if (id) {
            neighborId = id;
            break;
          }
        }
        cursor = cursor.nextElementSibling;
      }
      const startNeighborWidth =
        neighborId && initialPixels[neighborId] ? initialPixels[neighborId] : 0;

      let startX = 0;

      const onMouseMove = (e: MouseEvent) => {
        if (!startX) {
          startX = e.clientX;
          return;
        }
        if (total <= 0) return;

        const rawDelta = e.clientX - startX;
        const minDelta = MIN_COLUMN_WIDTH_PX - startActiveWidth;
        const maxDelta = neighborId
          ? startNeighborWidth - MIN_COLUMN_WIDTH_PX
          : rawDelta;
        const delta = Math.max(minDelta, Math.min(maxDelta, rawDelta));

        const nextFractions: Record<string, number> = {};
        for (const [id, px] of Object.entries(initialPixels)) {
          let newPx = px;
          if (id === columnId) newPx = startActiveWidth + delta;
          else if (id === neighborId) newPx = startNeighborWidth - delta;
          nextFractions[id] = newPx / total;
        }

        setState((prev) => ({
          ...prev,
          fitWidths: { ...prev.fitWidths, ...nextFractions },
        }));
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        setState((current) => {
          saveState(tableKey, current);
          return current;
        });
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [tableKey],
  );

  /** Scroll-mode resize: only the dragged column changes; table can widen. */
  const startScrollResize = useCallback(
    (columnId: string, th: HTMLElement) => {
      const startWidth = th.getBoundingClientRect().width;
      let startX = 0;

      const onMouseMove = (e: MouseEvent) => {
        if (!startX) {
          startX = e.clientX;
          return;
        }
        const delta = e.clientX - startX;
        const newPx = Math.max(MIN_COLUMN_WIDTH_PX, startWidth + delta);
        setState((prev) => ({
          ...prev,
          scrollWidths: { ...prev.scrollWidths, [columnId]: newPx },
        }));
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        setState((current) => {
          saveState(tableKey, current);
          return current;
        });
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [tableKey],
  );

  /**
   * Measures the widest currently-rendered content for one column and
   * returns the auto-fit target width (capped at AUTO_FIT_MAX_PX).
   */
  const computeAutoFitTargetPx = useCallback(
    (tableEl: HTMLElement, th: HTMLElement): number => {
      // Body rows have cells for the data columns only, not the header's action cells.
      const columnIndex = Array.from(th.parentElement?.children ?? [])
        .filter((cell) => cell.hasAttribute('data-column-id'))
        .indexOf(th);
      let maxContentPx = measureCellContentWidth(th);
      const bodyRows = tableEl.querySelectorAll('tbody tr');
      bodyRows.forEach((row) => {
        const cell = row.children[columnIndex];
        if (cell instanceof HTMLElement) {
          const w = measureCellContentWidth(cell);
          if (w > maxContentPx) maxContentPx = w;
        }
      });
      return Math.min(
        AUTO_FIT_MAX_PX,
        Math.max(MIN_COLUMN_WIDTH_PX, maxContentPx + AUTO_FIT_PADDING_PX),
      );
    },
    [],
  );

  /**
   * Excel-style auto-fit for every column. Computes intrinsic widths in one
   * pass and commits a single state update so the table re-renders once.
   */
  const autoFitAll = useCallback(
    (tableEl: HTMLElement) => {
      const headerRow = tableEl.querySelector('thead tr');
      if (!headerRow) return;
      const targets: Array<{ columnId: string; targetPx: number }> = [];
      Array.from(headerRow.children).forEach((child) => {
        if (!(child instanceof HTMLElement)) return;
        const columnId = child.getAttribute('data-column-id');
        if (!columnId) return;
        targets.push({
          columnId,
          targetPx: computeAutoFitTargetPx(tableEl, child),
        });
      });
      if (targets.length === 0) return;

      setState((prev) => {
        if (prev.mode === 'scroll') {
          const scrollWidths = { ...prev.scrollWidths };
          for (const { columnId, targetPx } of targets) {
            scrollWidths[columnId] = targetPx;
          }
          const next: TableLayoutState = { ...prev, scrollWidths };
          saveState(tableKey, next);
          return next;
        }
        const total = targets.reduce((s, t) => s + t.targetPx, 0);
        if (total <= 0) return prev;
        const fitWidths: Record<string, number> = { ...prev.fitWidths };
        for (const { columnId, targetPx } of targets) {
          fitWidths[columnId] = targetPx / total;
        }
        const next: TableLayoutState = { ...prev, fitWidths };
        saveState(tableKey, next);
        return next;
      });
    },
    [tableKey, computeAutoFitTargetPx],
  );

  /**
   * Excel-style auto-fit: size the column to the widest visible content
   * (header + currently rendered body cells). Targets only the column id;
   * other columns are untouched.
   */
  const autoFit = useCallback(
    (columnId: string, th: HTMLElement) => {
      const tableEl = th.closest('table');
      if (!tableEl) return;

      const targetPx = computeAutoFitTargetPx(tableEl, th);

      setState((prev) => {
        if (prev.mode === 'scroll') {
          const next: TableLayoutState = {
            ...prev,
            scrollWidths: { ...prev.scrollWidths, [columnId]: targetPx },
          };
          saveState(tableKey, next);
          return next;
        }
        const row = th.parentElement;
        if (!row) return prev;
        const pixels: Record<string, number> = {};
        let total = 0;
        for (const sibling of Array.from(row.children)) {
          if (!(sibling instanceof HTMLElement)) continue;
          const siblingId = sibling.getAttribute('data-column-id');
          if (!siblingId) continue;
          const w =
            siblingId === columnId
              ? targetPx
              : sibling.getBoundingClientRect().width;
          pixels[siblingId] = w;
          total += w;
        }
        if (total <= 0) return prev;
        const fitWidths: Record<string, number> = { ...prev.fitWidths };
        for (const [id, px] of Object.entries(pixels)) {
          fitWidths[id] = px / total;
        }
        const next: TableLayoutState = { ...prev, fitWidths };
        saveState(tableKey, next);
        return next;
      });
    },
    [tableKey, computeAutoFitTargetPx],
  );

  /**
   * Switches to the scroll layout keeping each column's current width, except
   * that a column squeezed below its content (many fit columns) widens to it.
   * Returns a function that restores the layout from before.
   */
  const switchToScroll = useCallback(
    (tableEl: HTMLElement) => {
      const widths: Record<string, number> = {};
      const headerRow = tableEl.querySelector('thead tr');
      Array.from(headerRow?.children ?? []).forEach((child) => {
        const columnId = child.getAttribute('data-column-id');
        if (!(child instanceof HTMLElement) || !columnId) return;
        const current = child.getBoundingClientRect().width;
        if (current === 0) return;
        widths[columnId] = Math.round(
          Math.max(current, computeAutoFitTargetPx(tableEl, child)),
        );
      });
      const previous = state;
      setState((prev) => {
        const next: TableLayoutState = {
          ...prev,
          mode: 'scroll',
          scrollWidths: { ...prev.scrollWidths, ...widths },
        };
        saveState(tableKey, next);
        return next;
      });
      return () => {
        setState(previous);
        saveState(tableKey, previous);
      };
    },
    [tableKey, computeAutoFitTargetPx, state],
  );

  /** Scroll layout: sizes the given columns to their content, like auto-fit, and stores the widths. */
  const fitColumnsToContent = useCallback(
    (tableEl: HTMLElement, columnIds: readonly string[]) => {
      const headerRow = tableEl.querySelector('thead tr');
      if (!headerRow) return;
      const widths: Record<string, number> = {};
      Array.from(headerRow.children).forEach((child) => {
        const columnId = child.getAttribute('data-column-id');
        // No width means no layout yet (or none at all, as in tests): nothing to measure.
        if (
          !(child instanceof HTMLElement) ||
          !columnId ||
          !columnIds.includes(columnId) ||
          child.getBoundingClientRect().width === 0
        )
          return;
        widths[columnId] = computeAutoFitTargetPx(tableEl, child);
      });
      if (Object.keys(widths).length === 0) return;
      setState((prev) => {
        const next: TableLayoutState = {
          ...prev,
          scrollWidths: { ...prev.scrollWidths, ...widths },
        };
        saveState(tableKey, next);
        return next;
      });
    },
    [tableKey, computeAutoFitTargetPx],
  );

  const fitColumnWidths = useMemo(
    () => fractionsToWidthMap(state.fitWidths),
    [state.fitWidths],
  );
  const scrollColumnWidths = useMemo(
    () => pixelsToWidthMap(state.scrollWidths),
    [state.scrollWidths],
  );

  return {
    layoutMode: state.mode,
    toggleLayoutMode,
    switchToScroll,
    columnWidths:
      state.mode === 'scroll' ? scrollColumnWidths : fitColumnWidths,
    startResize: state.mode === 'scroll' ? startScrollResize : startFitResize,
    autoFit,
    autoFitAll,
    fitColumnsToContent,
  };
}
